import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  Input,
  Tag,
  message,
  Popconfirm,
  Space,
  Divider,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getLemburList,
  createLembur,
  updateLembur,
  deleteLembur,
} from "../../api/transaksi/Lembur";

export default function LemburKaryawan() {
  const [data, setData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);

  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadingTable(true);
    try {
      const myData = await getLemburList(
        sessiData?.[0]?.user?.[0]?.employeeId || null
      );
      setData(myData.data);
    } catch (err) {
      message.error("Gagal memuat data lembur.");
    } finally {
      setLoadingTable(false);
    }
  };

  const openForm = (record = null) => {
    setEditingRecord(record);
    if (record) {
      form.setFieldsValue({
        ...record,
        ovt_tanggal_lembur: dayjs(record.ovt_tanggal_lembur),
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleFinish = async (values) => {
    setFormLoading(true);
    try {
      if (editingRecord) {
        const payload = {
          ovt_tanggal_lembur: values.ovt_tanggal_lembur.format("YYYY-MM-DD"),
          ovt_keterangan: values.ovt_keterangan,
          ovt_update_by: sessiData?.[0]?.user?.[0]?.fullname || null,
        };
        await updateLembur(editingRecord.ovt_id_lembur, payload);
        message.success("Data lembur berhasil diperbarui.");
      } else {
        const payload = {
          ovt_biaya_lembur: null,
          ovt_id_karyawan: sessiData?.[0]?.user?.[0]?.employeeId || null,
          ovt_tanggal_lembur: values.ovt_tanggal_lembur.format("YYYY-MM-DD"),
          ovt_keterangan: values.ovt_keterangan,
          ovt_status: "pending",
          ovt_status_pembayaran: "pending",
          ovt_created_by: sessiData?.[0]?.user?.[0]?.fullname || null,
        };
        await createLembur(payload);
        message.success("Pengajuan lembur berhasil dikirim.");
      }
      fetchData();
      setModalVisible(false);
      form.resetFields();
    } catch {
      message.error("Gagal mengirim pengajuan lembur.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Apakah Anda yakin ingin menghapus data lembur ini?",
      content: "Tindakan ini tidak dapat dibatalkan.",
      okText: "Hapus",
      okType: "danger",
      cancelText: "Batal",
      onOk: async () => {
        setLoadingTable(true);
        try {
          const res = await deleteLembur(id);
          res
            ? message.success("Data lembur berhasil dihapus.")
            : message.error(res.message);
          fetchData();
        } catch {
          message.error("Gagal menghapus data lembur.");
        } finally {
          setLoadingTable(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "Tanggal",
      dataIndex: "ovt_tanggal_lembur",
      width: 150,
    },
    {
      title: "Biaya",
      dataIndex: "ovt_biaya_lembur",
      width: 150,
      render: (val) => `Rp ${Number(val).toLocaleString("id-ID")}`,
    },
    {
      title: "Keterangan",
      dataIndex: "ovt_keterangan",
    },
    {
      title: "Status Pengajuan",
      dataIndex: "ovt_status",
      width: 50,
      filters: [
        { text: "Pending", value: "pending" },
        { text: "Disetujui", value: "disetujui" },
        { text: "Ditolak", value: "ditolak" },
      ],
      onFilter: (value, record) => record.ovt_status === value,
      render: (status) => {
        if (!status || status === "pending")
          return <Tag color="volcano">Pending</Tag>;
        const colorMap = {
          pending: "orange",
          disetujui: "green",
          ditolak: "red",
        };
        return <Tag color={colorMap[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Status Pembayaran",
      dataIndex: "ovt_status_pembayaran",
      width: 50,
      filters: [
        { text: "Tidak Dibayar", value: "tidak dibayar" },
        { text: "Lunas", value: "lunas" },
        { text: "Pending", value: "pending" },
      ],
      onFilter: (value, record) => record.ovt_status_pembayaran === value,
      render: (status) => {
        if (!status || status === "pending")
          return <Tag color="volcano">Pending</Tag>;

        const colorMap = {
          pending: "volcano",
          lunas: "blue",
          "tidak dibayar": "gray",
        };
        const labelMap = {
          pending: "Pending",
          lunas: "Lunas",
          "tidak dibayar": "Tidak Dibayar",
        };

        return (
          <Tag color={colorMap[status]}>{labelMap[status]?.toUpperCase()}</Tag>
        );
      },
    },
    {
      title: "Aksi",
      key: "aksi",
      width: 50,
      render: (_, record) => {
        const isEditable = record.ovt_status === "pending";
        return (
          <Space>
            <Button
              icon={<EditOutlined />}
              type="link"
              disabled={!isEditable}
              onClick={() => openForm(record)}
            >
              Edit
            </Button>
            <Button
              icon={<DeleteOutlined />}
              type="link"
              danger
              disabled={!isEditable}
              onClick={() => handleDelete(record.ovt_id_lembur)}
            >
              Hapus
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Divider>Input Pengajuan Lembur</Divider>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => openForm()}
        style={{ marginBottom: 16 }}
      >
        Ajukan Lembur
      </Button>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="ovt_id_lembur"
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
        loading={loadingTable}
      />

      <Modal
        title={editingRecord ? "Edit Lembur" : "Ajukan Lembur Baru"}
        open={modalVisible}
        confirmLoading={formLoading}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            label="Tanggal Lembur"
            name="ovt_tanggal_lembur"
            rules={[{ required: true, message: "Pilih tanggal lembur" }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Keterangan"
            name="ovt_keterangan"
            rules={[{ required: true, message: "Masukkan keterangan" }]}
          >
            <Input.TextArea placeholder="Contoh: Lembur input data..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
